import { useMemo, useState } from "react";
import type { ValidationErrors } from "../../types";
import type {
  MaterialCharacteristics,
  ProductType,
} from "@jasper/client-models";
import { IconCircleCheck, IconExclamationCircle } from "@tabler/icons-react";
import { useProductTranslations } from "@jasper/translations";
import { useTranslation } from "react-i18next";

type ItemsMaterialCharacteristics = Record<number, MaterialCharacteristics>;

interface Props {
  isShadeGlobal?: boolean;
  teeth: number[] | undefined;
  productType: ProductType;
  toothValidator: (
    tooth: number
  ) => ValidationErrors<MaterialCharacteristics> | undefined;
  globalErrors?: ValidationErrors<ItemsMaterialCharacteristics> | undefined;
}

export const useItemsMaterialCharacteristicsValidation = ({
  productType,
  teeth,
  toothValidator,
  globalErrors,
  isShadeGlobal = false,
}: Props) => {
  const { t } = useTranslation("forms");
  const { t: translateProduct } = useProductTranslations();

  const [validatedTeeth, setValidatedTeeth] = useState<string[]>([]);
  const [teethValidationErrors, setTeethValidationErrors] = useState<
    ValidationErrors<ItemsMaterialCharacteristics> | undefined
  >(undefined);

  const sortedTeeth = useMemo(() => teeth?.toSorted(), [teeth]);

  const isValidatedTooth = (tooth: number) =>
    validatedTeeth.includes(tooth.toString());

  const getToothErrors = (tooth: number) => {
    if (isShadeGlobal) {
      // When shade is global, we only return material errors at tooth level, shade errors are handled at global level
      const toothErrors =
        teethValidationErrors?.[tooth] ?? globalErrors?.[tooth];
      if (toothErrors?.material) {
        return { material: toothErrors.material };
      }
      return undefined;
    }

    return teethValidationErrors?.[tooth] ?? globalErrors?.[tooth];
  };

  const getToothStatus = (tooth: number) => {
    const normalizedProductType = productType.split(" ")[0];
    const toothDescription = `${translateProduct(normalizedProductType, { count: 1 })} ${tooth}`;
    const toothErrors = getToothErrors(tooth);
    if (toothErrors) {
      return {
        icon: <IconExclamationCircle color="red" />,
        title: `${toothDescription} : ${t("common.toProvide")}`,
        toothErrors,
      };
    }

    if (isValidatedTooth(tooth)) {
      return {
        icon: <IconCircleCheck color="green" />,
        title: `${toothDescription} : ${t("common.validated")}`,
      };
    }

    return { title: `${toothDescription} : ${t("common.toProvide")}` };
  };

  const validateTooth = (tooth: number) => {
    const errors = toothValidator(tooth);

    if (errors && (!isShadeGlobal || errors.material)) {
      setTeethValidationErrors(prev => ({
        ...prev,
        [tooth]: isShadeGlobal
          ? {
              material: errors.material,
            }
          : errors,
      }));
      return false;
    }

    setTeethValidationErrors(prev => ({
      ...prev,
      [tooth]: undefined,
    }));

    if (!validatedTeeth.includes(tooth.toString())) {
      setValidatedTeeth(prev => [...prev, tooth.toString()]);
    }

    return true;
  };

  const resetToothValidation = (tooth: number) => {
    setValidatedTeeth(prev => prev.filter(t => t !== String(tooth)));
  };

  return {
    sortedTeeth,
    getToothStatus,
    getToothErrors,
    validateTooth,
    validatedTeeth,
    resetToothValidation,
  };
};
